/**
 * O desenho do carrossel da Nex Lab no canvas 2D.
 *
 * Executa o que `src/arte/nexlab.ts` decide — toda coordenada vem de lá. Aqui
 * fica só o que precisa de um canvas de verdade: medir texto, recortar foto,
 * desfocar e pintar os SVGs do designer.
 *
 * A MESMA função desenha o preview, as miniaturas e a exportação, sempre a
 * 1080×1350. Preview menor reescalado poderia encolher o título num tamanho
 * diferente do que sai — o que se vê tem de ser o que sai.
 *
 * Os SVGs vêm de `/arte/nexlab/`, pela nossa origem: imagem de outra origem
 * contaminaria o canvas e o `toBlob()` passaria a lançar — a mesma armadilha
 * das fotos (docs/EDITOR-DE-ARTES.md §3.2).
 */
import {
  ALTURA,
  CARD_CAPA,
  CARD_FINAL,
  COR,
  DESFOQUE,
  FONTE,
  LARGURA,
  PESO,
  SETA,
  SUBTITULO_MAX,
  TAG,
  TEXTO_CAPA,
  TIPO,
  TITULO_MAX,
  TRACKING,
  faixas,
  linhaDoSubtitulo,
  pilula,
  recorte,
  tamanhoQueCabe,
  type Caixa,
  type FotoNaArte,
  type Slide
} from "../../arte/nexlab.js";

/** As peças fixas do sistema, desenhadas pelo designer. */
export interface Recursos {
  cardCapa: HTMLImageElement;
  cardFinal: HTMLImageElement;
  seta: HTMLImageElement;
  tag: HTMLImageElement;
}

/** As fotos já carregadas, por `media_assets.id`. */
export type Fotos = ReadonlyMap<number, HTMLImageElement>;

function carregarImagem(src: string): Promise<HTMLImageElement> {
  return new Promise((ok, erro) => {
    const img = new Image();
    img.addEventListener("load", () => ok(img));
    img.addEventListener("error", () =>
      erro(new Error(`Não foi possível carregar ${src}.`))
    );
    img.src = src;
  });
}

/**
 * Fontes e SVGs, ANTES do primeiro desenho.
 *
 * Fonte que chega tarde faz `measureText` devolver a métrica da fonte de
 * fallback: o título encolhe no tamanho errado e a pílula sai com a largura
 * errada, sem erro nenhum no console (docs/EDITOR-DE-ARTES.md §6).
 */
export async function carregarRecursos(): Promise<Recursos> {
  await Promise.all([
    document.fonts.load(`${PESO.titulo} ${TIPO.titulo}px ${FONTE.titulo}`),
    document.fonts.load(`${PESO.texto} ${TIPO.subtitulo}px ${FONTE.texto}`)
  ]);
  await document.fonts.ready;

  const [cardCapa, cardFinal, seta, tag] = await Promise.all([
    carregarImagem("/arte/nexlab/card-capa.svg"),
    carregarImagem("/arte/nexlab/card-final.svg"),
    carregarImagem("/arte/nexlab/seta.svg"),
    carregarImagem("/arte/nexlab/tag.svg")
  ]);
  return { cardCapa, cardFinal, seta, tag };
}

// ----------------------------------------------------------------- fotos

function pronta(img: HTMLImageElement | undefined): img is HTMLImageElement {
  return img !== undefined && img.complete && img.naturalWidth > 0;
}

function desenharFaixas(
  ctx: CanvasRenderingContext2D,
  fotos: readonly FotoNaArte[],
  imagens: Fotos
): void {
  const caixas = faixas(fotos.length);
  fotos.forEach((f, i) => {
    const c = caixas[i]!;
    const img = f.mediaId === null ? undefined : imagens.get(f.mediaId);
    if (!pronta(img)) {
      vazio(ctx, c, i + 1);
      return;
    }
    const r = recorte({ w: img.naturalWidth, h: img.naturalHeight }, c, f.foco);
    ctx.save();
    ctx.beginPath();
    ctx.rect(c.x, c.y, c.w, c.h);
    ctx.clip();
    ctx.drawImage(img, r.x, r.y, r.w, r.h);
    ctx.restore();
  });
}

/**
 * O slot sem foto. Só aparece no preview: a exportação é recusada antes
 * (ver `pendencias`), porque este retângulo iria para o Instagram.
 */
function vazio(ctx: CanvasRenderingContext2D, c: Caixa, n: number): void {
  ctx.fillStyle = COR.vazio;
  ctx.fillRect(c.x, c.y, c.w, c.h);
  ctx.save();
  ctx.strokeStyle = "rgba(165,99,255,0.35)";
  ctx.lineWidth = 2;
  ctx.setLineDash([14, 10]);
  ctx.strokeRect(c.x + 12, c.y + 12, c.w - 24, c.h - 24);
  ctx.restore();
  ctx.save();
  ctx.font = `${PESO.texto} 40px ${FONTE.texto}`;
  ctx.fillStyle = "rgba(255,255,255,0.45)";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(`foto ${n}`, c.x + c.w / 2, c.y + c.h / 2);
  ctx.restore();
}

// --------------------------------------------------------------- o vidro

/** O canvas aplica `ctx.filter`? Onde não aplica, o atributo é ignorado em silêncio. */
function temFiltro(ctx: CanvasRenderingContext2D): boolean {
  if (!("filter" in ctx)) return false;
  const antes = ctx.filter;
  ctx.filter = "blur(1px)";
  const ok = ctx.filter === "blur(1px)";
  ctx.filter = antes;
  return ok;
}

/**
 * Desfoca o que já está desenhado DENTRO do card — o efeito de vidro.
 *
 * Com `ctx.filter` quando o navegador tem; sem ele, reduzindo e ampliando a
 * área, que dá um desfoque parecido. O fallback existe porque o filtro do
 * canvas falha em silêncio onde não é suportado: sem ele, o card sairia
 * transparente sobre a foto nítida, e ninguém notaria até ver no perfil.
 */
function vidro(
  ctx: CanvasRenderingContext2D,
  card: { x: number; y: number; w: number; h: number; raio: number },
  raio: number
): void {
  // Copia a arte inteira como está: o desfoque precisa ler os pixels de fora
  // da borda, senão a beira do card escurece.
  const copia = document.createElement("canvas");
  copia.width = LARGURA;
  copia.height = ALTURA;
  const cc = copia.getContext("2d");
  if (!cc) return;
  cc.drawImage(ctx.canvas, 0, 0);

  ctx.save();
  ctx.beginPath();
  ctx.roundRect(card.x, card.y, card.w, card.h, card.raio);
  ctx.clip();

  if (temFiltro(ctx)) {
    ctx.filter = `blur(${raio}px)`;
    ctx.drawImage(copia, 0, 0);
    ctx.filter = "none";
  } else {
    const fator = Math.max(2, Math.round(raio / 2));
    const peq = document.createElement("canvas");
    peq.width = Math.ceil(LARGURA / fator);
    peq.height = Math.ceil(ALTURA / fator);
    const pc = peq.getContext("2d");
    if (pc) {
      pc.imageSmoothingQuality = "high";
      pc.drawImage(copia, 0, 0, peq.width, peq.height);
      ctx.imageSmoothingQuality = "high";
      ctx.drawImage(peq, 0, 0, LARGURA, ALTURA);
    }
  }
  ctx.restore();
}

// ----------------------------------------------------------------- texto

function fonte(familia: "titulo" | "texto", tamanho: number): string {
  return `${PESO[familia]} ${tamanho}px ${FONTE[familia]}`;
}

export interface Medidas {
  /** O tamanho em que o título saiu. Menor que o da especificação = encolheu. */
  tituloTamanho: number;
  subtituloTamanho: number;
}

/**
 * Largura e escrita com tracking, caractere a caractere.
 *
 * Mesma escolha de `src/ui/arte/desenho.ts`: `ctx.letterSpacing` resolveria
 * em uma linha, mas falha em silêncio onde não existe, e o texto sairia mais
 * largo sem ninguém ver. `tracking` é em px; n-1 espaços entre n letras.
 */
function larguraCom(
  ctx: CanvasRenderingContext2D,
  texto: string,
  tracking: number
): number {
  const n = Array.from(texto).length;
  return ctx.measureText(texto).width + (n > 1 ? tracking * (n - 1) : 0);
}

function escreverCom(
  ctx: CanvasRenderingContext2D,
  texto: string,
  x: number,
  y: number,
  tracking: number
): void {
  let cursor = x;
  for (const ch of texto) {
    ctx.fillText(ch, cursor, y);
    cursor += ctx.measureText(ch).width + tracking;
  }
}

function desenharTextoDaCapa(
  ctx: CanvasRenderingContext2D,
  capa: Extract<Slide, { tipo: "capa" }>["capa"]
): Medidas {
  const t = TEXTO_CAPA;
  ctx.save();
  ctx.fillStyle = COR.texto;
  ctx.textBaseline = "alphabetic";
  ctx.textAlign = "left";

  // A pílula do parceiro: a largura acompanha o nome. Antes do local,
  // porque é ela que diz até onde o local pode ir.
  ctx.font = fonte("texto", TIPO.parceiro);
  const parceiro = capa.parceiro.trim();
  const trParceiro = TRACKING.parceiro * TIPO.parceiro;
  const p = pilula(parceiro === "" ? 0 : larguraCom(ctx, parceiro, trParceiro));
  if (p) {
    ctx.fillStyle = COR.pilula;
    ctx.beginPath();
    ctx.roundRect(p.x, p.y, p.w, p.h, p.h / 2);
    ctx.fill();
    ctx.fillStyle = COR.texto;
    escreverCom(
      ctx,
      parceiro,
      p.x + t.pilula.respiro,
      t.pilula.linhaDeBase,
      trParceiro
    );
  }

  // Local — depois do `//`, que já vem no SVG. Encolhe antes de encostar
  // na pílula: os dois dividem a mesma linha.
  const local = capa.local.trim();
  if (local !== "") {
    ctx.font = fonte("texto", TIPO.local);
    const limite = (p ? p.x : t.pilula.direita) - 24 - t.local.x;
    const tam = tamanhoQueCabe(
      larguraCom(ctx, local, TRACKING.local * TIPO.local),
      TIPO.local,
      limite
    );
    ctx.font = fonte("texto", tam);
    escreverCom(ctx, local, t.local.x, t.local.y, TRACKING.local * tam);
  }

  // Título: numa linha só, encolhendo se não couber.
  ctx.font = fonte("titulo", TIPO.titulo);
  const tituloTamanho = tamanhoQueCabe(
    ctx.measureText(capa.titulo).width,
    TIPO.titulo,
    TITULO_MAX
  );
  ctx.font = fonte("titulo", tituloTamanho);
  ctx.fillText(capa.titulo, t.titulo.x, t.titulo.y);

  // Subtítulo, e a linha que o segue até a borda.
  ctx.font = fonte("texto", TIPO.subtitulo);
  const sub = capa.subtitulo.trim();
  const subtituloTamanho = tamanhoQueCabe(
    sub === "" ? 0 : ctx.measureText(sub).width,
    TIPO.subtitulo,
    SUBTITULO_MAX
  );
  ctx.font = fonte("texto", subtituloTamanho);
  let fim: number | null = null;
  if (sub !== "") {
    ctx.fillText(sub, t.subtitulo.x, t.subtitulo.y);
    fim = t.subtitulo.x + ctx.measureText(sub).width;
  }
  const linha = linhaDoSubtitulo(fim);
  if (linha) {
    ctx.strokeStyle = COR.linha;
    ctx.lineWidth = t.linha.espessura;
    ctx.beginPath();
    ctx.moveTo(linha.x1, linha.y);
    ctx.lineTo(linha.x2, linha.y);
    ctx.stroke();
  }

  ctx.restore();
  return { tituloTamanho, subtituloTamanho };
}

// ---------------------------------------------------------------- slides

function caixa(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  c: Caixa
): void {
  ctx.drawImage(img, c.x, c.y, c.w, c.h);
}

/**
 * Desenha um slide inteiro. Devolve as medidas do texto quando é a capa, para
 * a tela poder avisar que o título encolheu.
 */
export function desenharSlide(
  ctx: CanvasRenderingContext2D,
  slide: Slide,
  imagens: Fotos,
  r: Recursos
): Medidas | null {
  ctx.clearRect(0, 0, LARGURA, ALTURA);
  desenharFaixas(ctx, slide.fotos, imagens);

  switch (slide.tipo) {
    case "capa": {
      vidro(ctx, CARD_CAPA, DESFOQUE.capa);
      caixa(ctx, r.cardCapa, CARD_CAPA);
      return desenharTextoDaCapa(ctx, slide.capa);
    }
    case "interna":
      caixa(ctx, r.tag, TAG);
      caixa(ctx, r.seta, SETA);
      return null;
    case "final":
      vidro(ctx, CARD_FINAL, DESFOQUE.final);
      caixa(ctx, r.cardFinal, CARD_FINAL);
      return null;
  }
}
